import flatpickr from 'flatpickr';
import { Japanese } from "flatpickr/dist/l10n/ja.js"

let masterData = {}

const userCheckState = {
  area: {},
  prefecture: {},
  region: {},
  category: {},
  maker: {}
}

const keyMap = {
  area: {
    keyName: "area_slug[]",
    pattern: /area_slug\[\d*\]/,
    query: "input[name='area_slug[]']"
  },
  pref: {
    keyName: "prefecture_slug[]",
    pattern: /prefecture_slug\[\]/,
    query: "input[name='prefecture_slug[]']"
  },
  region: {
    keyName: 'region_slug[]',
    pattern: /region_slug\[\]/,
    query: 'input[name="region_slug[]"]'
  }
}

//
// 検索パラメータ数の制限（サーバー側 config/search_guard.php と同期）
// ※上限を超える選択はメッセージを表示して拒否する
//
const SEARCH_LIMIT_FALLBACK = {
  areas: 3, prefectures: 5, regions: 5, categories: 5, makers: 5, total: 12, period_months: 3
}

function searchLimits() {
  return Object.assign({}, SEARCH_LIMIT_FALLBACK, window.__SEILOO_SEARCH_LIMITS__ || {})
}

// 検索条件のinput名 → 上限のキー・表示ラベル・選択状況の保持先
const limitMap = {
  "area_slug[]":       { limitKey: 'areas',       label: 'エリア',   state: () => userCheckState.area },
  "prefecture_slug[]": { limitKey: 'prefectures', label: '都道府県', state: () => userCheckState.prefecture },
  "region_slug[]":     { limitKey: 'regions',     label: '市区町村', state: () => userCheckState.region },
  "category_slug[]":   { limitKey: 'categories',  label: 'カテゴリ', state: null },
  "maker_slug[]":      { limitKey: 'makers',      label: 'メーカー', state: null }
}

// userCheckState 上で選択中の件数
function countCheckedState(state) {
  return Object.keys(state).filter(key => state[key] && state[key].checked).length
}

// DOM 上で選択中の件数
function countCheckedDom(name) {
  return document.querySelectorAll(`input[name="${name}"]:checked`).length
}

/**
 * 対象種別の確定済み選択数を返す
 * ※エリア/都道府県/市区町村は userCheckState、カテゴリ/メーカーは DOM が選択状況の保持先
 * @param {string} name inputのname
 * @param {string|null} changingName 変更中(まだ確定していない)のinputのname
 */
function countConfirmedSelection(name, changingName) {
  const entry = limitMap[name]
  if (!entry) return 0

  if (entry.state) {
    // capture フェーズで判定する ※変更中のinputは未反映反映されていない
    return countCheckedState(entry.state())
  }

  // DOM はチェック済みの状態になっているため、変更中のinput分を除外する
  return countCheckedDom(name) - (name === changingName ? 1 : 0)
}

/**
 * 上限超過時のメッセージを表示する
 */
function showSearchLimitMessage(input, message) {
  const container = input.closest('.panel-body-second') || input.closest('.panel') || input.form
  if (!container) return

  let box = container.querySelector('.search-limit-message')
  if (!box) {
    box = document.createElement('div')
    box.className = 'alert alert-warning search-limit-message'
    box.setAttribute('role', 'alert')
    container.prepend(box)
  }

  box.textContent = message
  box.style.display = ''

  clearTimeout(box.dataset.timerId)
  box.dataset.timerId = setTimeout(() => { box.style.display = 'none' }, 5000)
}

/**
 * 上限を超える選択を拒否する
 */
function guardSelectionLimit(e) {
  const input = e.target

  if (!input || input.type !== 'checkbox' || !limitMap[input.name]) return
  if (!input.checked) return // 選択解除は常に許可

  const limits = searchLimits()
  const entry = limitMap[input.name]
  const typeLimit = Number(limits[entry.limitKey])

  // 種別ごとの上限
  if (Number.isFinite(typeLimit) && typeLimit >= 0) {
    const prospective = countConfirmedSelection(input.name, input.name) + 1
    if (prospective > typeLimit) {
      rejectSelection(e, input, `${entry.label}は${typeLimit}件まで選択できます。`)
      return
    }
  }

  // 検索条件の総数の上限
  const totalLimit = Number(limits.total)
  if (Number.isFinite(totalLimit) && totalLimit > 0) {
    const total = Object.keys(limitMap)
      .reduce((sum, name) => sum + countConfirmedSelection(name, input.name), 0) + 1

    if (total > totalLimit) {
      rejectSelection(e, input, `検索条件は合計${totalLimit}件まで選択できます。`)
    }
  }
}

function rejectSelection(e, input, message) {
  input.checked = false
  e.stopPropagation()
  e.preventDefault()
  showSearchLimitMessage(input, message)
}

/**
 * 送信時の最終チェック
 * ※DOM更新前呼び出すこと
 * @returns {string|null} 超過している場合はメッセージ
 */
function validateSearchLimits() {
  const limits = searchLimits()
  let total = 0

  for (const name of Object.keys(limitMap)) {
    const count = countConfirmedSelection(name, null)
    total += count

    const typeLimit = Number(limits[limitMap[name].limitKey])
    if (Number.isFinite(typeLimit) && typeLimit >= 0 && count > typeLimit) {
      return `${limitMap[name].label}は${typeLimit}件まで選択できます。`
    }
  }

  const totalLimit = Number(limits.total)
  if (Number.isFinite(totalLimit) && totalLimit > 0 && total > totalLimit) {
    return `検索条件は合計${totalLimit}件まで選択できます。`
  }

  return null
}

document.addEventListener('DOMContentLoaded', async () => {
  // @type {HTMLFormElement}
  const form = document.querySelector('#sidebar-search-form');
  masterData = await getEventSearchData();
  const isNotEventsOrArea = !assertEventPath(location.pathname)

  handlerByEventPage(isNotEventsOrArea)

  initAreaSelector()
  initKeyWordPanel()
  initResetBtn()
  addCloseEventWhenClickOutSidePanel()

  // 上限を超える選択を拒否する ※各inputのchangeハンドラより先に判定するためcaptureで登録
  form.addEventListener('change', guardSelectionLimit, true);

  form.addEventListener('submit', (e) => {
    handleOnSubmit(e)
  });

  document.querySelectorAll('.panel').forEach(panel => {

    panel.querySelectorAll('.selected-box').forEach(selectBox => {
      selectBox.addEventListener('click', function (e) {
        const prevActivePanel = document.querySelector(".panel-body-second-active")
        const willActivePanel = e.currentTarget.parentElement.querySelector('.panel-body-second')
        deActivePanel(prevActivePanel)

        activePanel(willActivePanel)

        // panel-body-second 表示中はクリック/ホバーした検索窓の枠をハイライト(.active)する
        if (willActivePanel) {
          const inner = e.currentTarget.querySelector('.selected-box-inner')
          if (inner) {
            inner.classList.add('active')
          }
        }
      });
      selectBox.addEventListener("mouseover", function (e) {
        e.currentTarget.dispatchEvent(new Event('click'))
      });
    });

    panel.querySelectorAll('.panel-footer button').forEach(button => { // 閉じるボタンを押すと閉じる
      button.addEventListener('click', function (e) {
        deActivePanel(e.currentTarget.closest('.panel-body-second'))
      });
    });

    areasSection(panel, masterData.areas, masterData.prefectures, masterData.regions);
    datesSection(form, panel, isNotEventsOrArea);
    categoriesSection(panel, masterData.categories);
    makersSection(panel, masterData.makers);
  });

  restoreSearchParams(masterData)
});


// **どこかをクリックすると、クリックまたはホバーで開いた .panel-body-second を閉じる**
function addCloseEventWhenClickOutSidePanel() {
  document.addEventListener('click', function (event) {
    const isClickOutSide = event.target.closest('.panel') === null
      && event.target.closest('.flatpickr-calendar') === null

    if (isClickOutSide) {
      deActivePanel(document.querySelector(".panel-body-second-active"))
    }
  });
}

/**
 * @param {FormData} formData
 */
function removeEmptyStringFormDataKey(formData){
  Array.from(formData.keys()).map(key => {
    if(formData.get(key) === '') {
      formData.delete(key)
    }
  })
}

function checkedAllBoxes(formData, masterDataSize){
  const result = formData.length === masterDataSize
  return result
}

/**
 * @param {FormData} formData
 */
function checkSameDefaultSearchParam(formData, key, masterDataSize) {

  const arg = formData
  removeEmptyStringFormDataKey(arg)

  const distinctKeys = Array.from(new Set(arg.keys()))

  if(distinctKeys.length !== 1) {
    return false
  }

  if(!arg.has(key)) {
    return false
  }

  if(!checkedAllBoxes(formData.getAll(key),masterDataSize)) {
    return false
  }

  return true;
}

function initResetBtn() {
  const resetAllCondition = document.getElementById('reset-all-condition')
  resetAllCondition.addEventListener("click", redirectEventsPageWithNonQuery)
}

function initKeyWordPanel() {
  const root =  document.querySelector(".panel-keyword")
  const input = root.querySelector("input[name='q']")
  const submit = root.querySelector("button[type='submit']")

  input.addEventListener("focus", () => {
    // NOTE: キーワード入力欄focus時もパネル同様他パネルを閉じる
    const prevActivePanel = document.querySelector(".panel-body-second-active")
    deActivePanel(prevActivePanel)
  })

  input.addEventListener("change", (e) => {
    if(e.target.value.length > 0) {
      activateNarrowDownBtn(submit)
    }

    if(e.target.value.length === 0) {
      deActivateNarrowDownBtn(submit)
    }
  })

  input.dispatchEvent(new Event('change'))
}

function handlerByEventPage(isNotEventsOrArea) {
  // ディレクトリが対象でない場合にクラスを追加
  if (isNotEventsOrArea) {
    document.querySelectorAll('.panel-body-second').forEach(panelBodySecond => {
      panelBodySecond.classList.add('panel-body-second-other');
    });
  }
}

function initAreaSelector() {
  const root = document.querySelector(".panel-areas > .panel-body")
  const pref = root.querySelector(".selectedPrefecture")
  const region = root.querySelector(".selectedRegion")
  const submit = root.querySelector("button[type='submit']")

  changeClickableStat(pref, false)
  changeClickableStat(region, false)
  deActivateNarrowDownBtn(submit)

  const observerOpt = { attributes: true, attributeFilter: ['title'] }

  // 市区町村制御
  createPrefObserver(observerOpt)
  // 都道府県制御
  createAreaObserver(observerOpt)
}

function getRegionListGroupedByPrefId() {
  const result = Map.groupBy(masterData.regions, ({prefecture_id}) => prefecture_id)
  return result
}

function getPrefListGroupedByAreaId() {
  const result = Map.groupBy(masterData.prefectures, ({area_id}) => area_id)
  return result
}

function createAreaObserver(opt) {
  const root = document.querySelector(".panel-areas > .panel-body")
  const area = root.querySelector(".selectedArea #selectedItems")
  const pref = root.querySelector(".selectedPrefecture")

  const observer = new MutationObserver(() => {
    const hasChild = Boolean(area.childElementCount)

    if (hasChild) {
      changeClickableStat(pref, true)
    }

    if (!hasChild) {
      observer.disconnect()
      doReset("area")
      observer.observe(area, opt)
    }
  })

  observer.observe(area, opt)
}

function createPrefObserver(opt) {
  const root = document.querySelector(".panel-areas > .panel-body")
  const pref = root.querySelector(".selectedPrefecture #selectedItems")
  const region = root.querySelector(".selectedRegion")

  const observer = new MutationObserver(() => {
    const hasChild = Boolean(pref.childElementCount)

    if (hasChild) {
      changeClickableStat(region, true)
    }

    if (!hasChild) {
      observer.disconnect()
      doReset("pref")
      observer.observe(pref, opt)
    }
  })

  observer.observe(pref, opt)
}


function doReset(level) {
  const root = document.querySelector(".panel-areas > .panel-body")
  const area = root.querySelector(".selectedArea #selectedItems")
  const pref = root.querySelector(".selectedPrefecture")
  const region = root.querySelector(".selectedRegion")

  const messageMap = {
    area: "地方を選択",
    pref: "都道府県を選択",
    region: "市区町村を選択",
  }

  const actionMap = {
    area: () => {
      area.setAttribute("title", "")
      area.textContent = messageMap["area"]
      doReset("pref")
    },
    pref: () => {
      doReset("region")
      pref.querySelector("#selectedItems").setAttribute("title", "")
      pref.querySelector("#selectedItems").replaceChildren()
      pref.querySelector("#selectedItems").textContent = messageMap["pref"]
      changeClickableStat(pref, false)
    },
    region: () => {
      region.querySelector("#selectedItems").setAttribute("title", "")
      region.querySelector("#selectedItems").replaceChildren()
      region.querySelector("#selectedItems").textContent = messageMap["region"]
      changeClickableStat(region, false)
    },
  }

  actionMap[level]()
}


// NOTE: セレクターがdivタグで、disable属性が効果なし。そのためdisable属性に相当する処理をcssにて実装
function changeClickableStat(element, enable) {
  const styleMap = {
    false: {
      'cursor': 'not-allowed',
      'pointerEvent': 'none',
      'backgroundColor': '#d0d0d0'
    },
    true: {
      'cursor': 'pointer',
      'pointerEvent': 'auto',
      'backgroundColor': null
    }
  }

  element.style.cursor = styleMap[enable]['cursor']
  element.style.pointerEvents = styleMap[enable]['pointerEvent']
  element.style.backgroundColor = styleMap[enable]['backgroundColor']
}

function activateNarrowDownBtn(element) {
  element.removeAttribute('disabled')
  element.classList.add('active')
}

function deActivateNarrowDownBtn(element) {
  element.setAttribute('disabled', "")
  element.classList.remove('active')
}

// 検索窓（各パネルのセレクトボックス／日付）の枠ハイライト(.active)対象要素
function searchBoxHighlightTargets() {
  return document.querySelectorAll(
    '.panel-areas .selected-box-inner, '
    + '.panel-categories .selected-box-inner, '
    + '.panel-makers .selected-box-inner, '
    + '.date-select .calendar-container .date-output'
  )
}

// すべての検索窓ハイライトを解除する
function clearSearchBoxHighlight() {
  searchBoxHighlightTargets().forEach(el => el.classList.remove('active'))
}

function activePanel(panel) {
  if(!panel) {
    return
  }
  panel.classList.add("panel-body-second-active")
}

function deActivePanel(panel) {
  // panel-body-second を閉じるときは検索窓の枠ハイライトも解除する
  clearSearchBoxHighlight()

  if(!panel) {
    return
  }
  panel.classList.remove("panel-body-second-active")
}

function assertEventPath(path) {
  const pat = /\/(events|area)/
  const result = pat.test(path)
  return result
}

async function getEventSearchData() {
  return fetch('/api/v1/event-search-data')
    .then(response => {
      return response.json();
    });
}

function excludeFromSearchQuery(element) {
  element.disabled = true
}

function redirectEventsPageWithNonQuery() {
  location.href = '/events'
}

function handleOnSubmit(e) {
    /** @type {HTMLFormElement} */
    const form = e.target;

    e.preventDefault()

    // 検索パラメータ数の上限チェック ※超過している場合は検索を実行しない
    // NOTE: 以降はDOMを書き換えるため、必ず書き換え前に判定する
    const limitMessage = validateSearchLimits()
    if (limitMessage) {
      showSearchLimitMessage(form.querySelector('input[type="checkbox"]:checked') || form, limitMessage)
      return false
    }

    deActivePanel(document.querySelector(".panel-body-second-active"))

    // NOTE: エリア選択は動的に要素が変化するためsubmit前にinputを復元する処理が必要
    document.getElementById("areas-list").replaceChildren()

    for (const key in userCheckState.area) {
      const stored = userCheckState.area[key];
      if (stored.checked) {
        form.appendChild(createFormHiddenInput(form, keyMap.area.keyName, key));
      }
    }

    // 都道府県の選択状況を復元
    for (const key in userCheckState.prefecture) {
      const stored = userCheckState.prefecture[key];
      if (stored.checked) {
        form.appendChild(createFormHiddenInput(form, keyMap.pref.keyName, key));
      }
    }

    // 市区町村グループの選択状況を復元
    for (const key in userCheckState.region) {
      const stored = userCheckState.region[key];
      if (stored.checked) {
        form.appendChild(createFormHiddenInput(form, keyMap.region.keyName, key));
      }
    }

    const formData = new FormData(form);

    //
    // 特定パターンの検索条件の場合、リダイレクト or 条件の除去を行う
    // 対象は「カテゴリ」「メーカーブランド」
    //
    // パターン1 全ての選択肢を選択かつ他の条件が指定されていない
    // →eventsへリダイレクトする
    // パターン2 全ての選択肢を選択かつ他の検索条件を指定
    // →全ての選択肢を選択している条件をクエリパラメータから除外
    //

    const categoryKey = "category_slug[]"
    const makerKey = "maker_slug[]"

    if(checkSameDefaultSearchParam(formData, categoryKey, masterData.categories.length)) {
      redirectEventsPageWithNonQuery()
      return false
    }

    if(checkSameDefaultSearchParam(formData,makerKey , masterData.makers.length)) {
      redirectEventsPageWithNonQuery()
      return false
    }

    if(checkedAllBoxes(formData.getAll(categoryKey), masterData.categories.length)) {
      document.querySelectorAll(`input[name='${categoryKey}']`).forEach(item => {
        excludeFromSearchQuery(item)
      })
    }

    if(checkedAllBoxes(formData.getAll(makerKey), masterData.makers.length)) {
      document.querySelectorAll(`input[name='${makerKey}']`).forEach(item => {
        excludeFromSearchQuery(item)
      })
    }

    const isSingleArea = formData.has(keyMap.area.keyName) && formData.getAll(keyMap.area.keyName).length === 1
    const isSearchSingleAreaWithNoQueries =  isSingleArea && [...formData.keys()].length === 1

    // NOTE: 1エリアのみの検索時末尾に「?」が付与されないようにformを経由せず遷移させる
    if (isSearchSingleAreaWithNoQueries) {
      const areaInfo = masterData.areas.find(area => area.slug === formData.get(keyMap.area.keyName))
      location.href = `/area/${encodeURIComponent(areaInfo.slug)}`;
      return false
    }
    // エリア指定が存在する場合はその他クエリを維持したまま無条件でエリアページに遷移
    if (formData.has(keyMap.area.keyName) && formData.getAll(keyMap.area.keyName).length === 1) {
      const areaInfo = masterData.areas.find(area => area.slug === formData.get(keyMap.area.keyName))
      form.action = '/area/' + encodeURIComponent(areaInfo.slug);

      // クエリーストリングからエリアを除去する
      form.querySelectorAll(keyMap.area.query).forEach(input => excludeFromSearchQuery(input));
    } else if (formData.has(keyMap.area.keyName) && formData.getAll(keyMap.area.keyName).length > 1) {

      // 複数エリア指定時は指定エリアをarea_slug[]としてeventsページに遷移
      form.querySelectorAll(keyMap.area.query).forEach(input => input.name = keyMap.area.keyName);
    }

    // 未入力のinputパラメーターをクエリーストリングから除外する
    Array.from(form.querySelectorAll("input"))
         .filter(e => e.value === '')
         .forEach((input) => excludeFromSearchQuery(input))

    // 都道府県パラメーターの正規化
    if (formData.getAll(keyMap.pref.keyName).length > 1) {
      form.querySelectorAll(keyMap.pref.query).forEach(input => input.name = keyMap.pref.keyName);
    }

    // 市区町村パラメーターの正規化
    form.querySelectorAll(keyMap.region.query).forEach(input => input.name = keyMap.region.keyName);

    form.submit()

  }

  function createFormHiddenInput(form, name, value) {
    const input = form.ownerDocument.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    return input;
  }

  function toggleConfirmBtn(panel, isActive) {
    const ConfirmBtn = panel.querySelector('button[type="submit"]');
    if (ConfirmBtn) {
      if (isActive) {
        activateNarrowDownBtn(ConfirmBtn)
      } else {
        deActivateNarrowDownBtn(ConfirmBtn)
      }
    }
  }

  // 絞り込みボタン　選択窓に要素が入ったときに.active付与・解除
  function toggleCfbActive(panel) {
    // それぞれのpanelの適用
    let isActive = false;
    const areaBox = panel.querySelector(".panel-areas .selectedArea #selectedItems");
    const catBox = panel.querySelector(".panel-categories #selectedItems");
    const makerBox = panel.querySelector(".panel-makers #selectedItems");
    const dateStartBox = panel.querySelector(".panel-dates #dateStartSelect");
    const dateEndBox = panel.querySelector(".panel-dates #dateEndSelect");
    if ((areaBox && areaBox.children.length > 0) || (catBox && catBox.childElementCount > 0) || (makerBox && makerBox.children.length > 0) || (dateStartBox && dateStartBox.childElementCount > 0) || (dateEndBox && dateEndBox.childElementCount > 0)) {
      isActive = true;
    }
    toggleConfirmBtn(panel, isActive);
  }

  // カテゴリー・メーカー選択窓更新用（DOMのチェックボックスから更新）
  function updateSelectedItems(panel) {
    const selectors = {
      category: ".panel-categories #selectedItems",
      maker: ".panel-makers #selectedItems"
    };
    const selectedBoxes = Object.fromEntries(
      Object.entries(selectors).map(([key, selector]) => [key, panel.querySelector(selector)])
    );
    // クリア
    Object.values(selectedBoxes).forEach(box => {
      if (box) box.innerHTML = "";
    });
    // チェックボックスの状態から更新（name属性が category_slug[] / maker_slug[] のもの）
    const nameToKey = {
      "category_slug[]": "category",
      "maker_slug[]": "maker"
    };
    panel.querySelectorAll('input[type="checkbox"]').forEach(checkbox => {
      if (checkbox.checked) {
        const key = nameToKey[checkbox.name];
        if (key && selectedBoxes[key]) {
          let item = document.createElement("li");
          item.textContent = checkbox.dataset.name;
          selectedBoxes[key].appendChild(item);
        }
      }
    });
    // タイトルとデフォルトメッセージ（必要に応じて調整）
    Object.entries(selectedBoxes).forEach(([key, box]) => {
      if (box) {
        let names = [];
        Array.from(box.children).forEach(child => {
          names.push(child.textContent);
        });
        box.title = names.join(", ");
        if (names.length === 0) {
          if (key === "category") box.textContent = "カテゴリーを選択";
          if (key === "maker") box.textContent = "メーカーブランドを選択";
        }
      }
    });
    toggleCfbActive(panel);// 絞り込みボタンに選択窓に要素が入ったときに.active付与・解除
  }

  // エリア（地方、都道府県、市区町村）選択窓更新用（保存状態から更新）
  // ★ updateAreaSelectedItems() は areasSection() 内で保存している panel._savedAreaChecks 等を参照します
  function updateAreaSelectedItems(panel) {
    const selectors = {
      area: ".panel-areas .selectedArea #selectedItems",
      prefecture: ".panel-areas .selectedPrefecture #selectedItems",
      region: ".panel-areas .selectedRegion #selectedItems"
    };
    const selectedBoxes = Object.fromEntries(
      Object.entries(selectors).map(([key, selector]) => [key, panel.querySelector(selector)])
    );
    Object.values(selectedBoxes).forEach(box => {
      if (box) box.innerHTML = "";
    });
    // 保存状態から各選択窓を再構築
    if (userCheckState.area) {
      Object.values(userCheckState.area).forEach(obj => {

        if (obj.checked) {
          let li = document.createElement("li");
          li.textContent = obj.name || obj.areaId;
          selectedBoxes.area && selectedBoxes.area.appendChild(li);
        }
      });
    }
    if (userCheckState.prefecture) {
      Object.values(userCheckState.prefecture).forEach(obj => {
        if (obj.checked) {
          let li = document.createElement("li");
          li.textContent = obj.name || obj.prefectureId;
          selectedBoxes.prefecture && selectedBoxes.prefecture.appendChild(li);
        }
      });
    }
    if (userCheckState.region) {
      Object.values(userCheckState.region).forEach(obj => {
        if (obj.checked) {
          let li = document.createElement("li");
          li.textContent = obj.name || obj.regionId;
          selectedBoxes.region && selectedBoxes.region.appendChild(li);
        }
      });
    }
    // タイトル・デフォルトメッセージ
    Object.entries(selectedBoxes).forEach(([key, box]) => {
      if (box) {
        let names = [];
        if (key === "area" && userCheckState.area) {
          Object.values(userCheckState.area).forEach(obj => {
            if (obj.checked) names.push(obj.name || obj.areaId);
          });
        }
        if (key === "prefecture" && userCheckState.prefecture) {
          Object.values(userCheckState.prefecture).forEach(obj => {
            if (obj.checked) names.push(obj.name || obj.prefectureId);
          });
        }
        if (key === "region" && userCheckState.region) {
          Object.values(userCheckState.region).forEach(obj => {
            if (obj.checked) names.push(obj.name || obj.regionId);
          });
        }
        box.title = names.join(", ");
        if (names.length === 0) {
          if (key === "area") box.textContent = "地方を選択";
          if (key === "prefecture") box.textContent = "都道府県を選択";
          if (key === "region") box.textContent = "市区町村を選択";
        }
      }
    });

    // 絞り込みボタンに選択窓に要素が入ったときに.active付与・解除
    toggleCfbActive(panel);
  }

  // カテゴリー・メーカーformのinput要素管理
  /**
 * 汎用チェックボックス項目生成関数
 * @param {Object} item - 各項目のオブジェクト（例: maker, category, area, prefecture, region）
 * @param {Object} options - 以下のプロパティを持つオプションオブジェクト
 *   - inputName: チェックボックスの name 属性（例："maker_slug[]"）
 *   - labelTextProp: 項目名を取り出すプロパティ名（例:"name"）
 *   - slugProp: チェックボックスの value と id に使うプロパティ名（例:"slug"）
 *   - dataAttributes: オブジェクト。キーが付与する属性名（そのままsetAttributeする場合も可）、値が item のプロパティ名
 *       例: { "data-area-id": "id" } なら item.id を "data-area-id" 属性にセットする
 *   - fallbackSlug: true の場合、item[slugProp]が falsy なら、"fallbackSlugPrefix" + item[idProp] を id/value とする
 *   - fallbackSlugPrefix: fallbackSlug=true のときに使うプレフィックス（例:"region_"）
 *   - idProp: fallback用。item の一意な識別子となるプロパティ名（例:"id"）
 * @returns {HTMLElement} 生成された li 要素
 */
  function createCheckboxItem(item, options) {
    const li = document.createElement("li");
    li.classList.add("btn-default");

    const label = document.createElement("label");
    label.classList.add("search-list-item");

    // id と value を決定
    let inputId;
    if (options.fallbackSlug && !item[options.slugProp]) {
      inputId = options.fallbackSlugPrefix + item[options.idProp];
    } else {
      inputId = item[options.slugProp];
    }

    label.htmlFor = inputId;
    label.textContent = item[options.labelTextProp];

    const input = document.createElement("input");
    input.type = "checkbox";
    input.id = inputId;
    input.name = options.inputName;
    input.value = inputId;

    // data属性の設定（キーにハイフンが含まれている場合は setAttribute を使う）
    if (options.dataAttributes) {
      for (let attr in options.dataAttributes) {
        const prop = options.dataAttributes[attr];
        if (attr.indexOf("-") !== -1) {
          input.setAttribute(attr, item[prop]);
        } else {
          input.dataset[attr] = item[prop];
        }
      }
    }

    // label の先頭に input を挿入
    label.prepend(input);
    li.appendChild(label);

    return li;
  }

  function doRecusiveUnselectRelatedAreaCondtion(areaId) {
    const prefList = getPrefListGroupedByAreaId()
    const slugList = prefList.get(areaId).map((pref) => {
      return pref.slug
    })

    const deletionPrefList = Object.keys(userCheckState.prefecture).filter((pref) => slugList.includes(pref))

    deletionPrefList.map(key => {
      doUnselectRelatedRegionCondtion(parseInt(userCheckState.prefecture[key].prefectureId))
      delete userCheckState.prefecture[key]
    })
  }

  function doUnselectRelatedRegionCondtion(prefId) {
    const regionList = getRegionListGroupedByPrefId()
    const slugList = regionList.get(prefId).map((region) => {
      return region.slug
    })

    const idList = regionList.get(prefId).map((region) => {
      return region.id
    })
    const outSideRegionList = Object.keys(userCheckState.region).filter((region) => slugList.includes(region) || idList.includes(parseInt(region)))
    outSideRegionList.map(key => delete userCheckState.region[key])

  }

  // 「開催エリアで探す」関数
  function areasSection(panel, areas, prefectures, regions) {
    if (!panel.classList.contains('panel-areas')) {
      return
    }

    let currentListType = 'area';


    // panelBodySecond内にエリアリストを生成
    function buildAreaList(areas) {
      const areasList = panel.querySelector('#areas-list'); //panelBodySecond ul
      if (!areasList) return;
      areasList.dataset.type = "area"
      areasList.innerHTML = ""; //最初にpanelBodySecond ul内をクリア

      const limit = 3;
      areas.forEach(area => { //areas->地方のオブジェクト配列
        const list = document.createElement('li');
        list.classList.add('btn-default');

        const label = document.createElement('label');
        label.classList.add('search-list-item')
        label.htmlFor = area.slug;
        label.textContent = area.name;

        const input = document.createElement('input');
        input.type = 'checkbox';
        input.id = area.slug;
        input.name = keyMap.area.keyName;
        input.value = area.slug;
        input.dataset.name = area.name;
        input.setAttribute("data-area-id", area.id);

        if (userCheckState.area[area.slug] && userCheckState.area[area.slug].checked) {
          input.checked = true; //inputはチェック状態 = チェックを復元
        }

        // 入力変更イベントで保存更新
        input.addEventListener('change', function () {

          // NOTE: 下位条件削除
          if(!this.checked) {
            const areaId = parseInt(this.getAttribute('data-area-id'))
            doRecusiveUnselectRelatedAreaCondtion(areaId)
          }

          userCheckState.area[this.value] = {
            checked: this.checked,
            areaId: this.getAttribute("data-area-id"),
            name: this.dataset.name
          };

          blockOverLimitAreaSelection(limit)
          updateAreaSelectedItems(panel);

          // 「現在areaパネルを見ているなら」即座にリストを再描画(すぐにチェック状態を反映させる)
          if (currentListType === 'area') {
            buildAreaList(areas);
          }
        });

        label.prepend(input);
        list.appendChild(label);
        areasList.appendChild(list);
      });

      blockOverLimitAreaSelection(limit)
      updateAreaSelectedItems(panel); // チェックボックスにチェックを入れたら名称を選択窓に格納
    }

    // 現在のareaリストのチェック状態を保存する
    function saveAreaState() {
      const checkboxes = panel.querySelectorAll(keyMap.area.query);
      checkboxes.forEach(chk => {
        userCheckState.area[chk.value] = { //保存されたareaのチェックボックスでchkをkeyとしてvalueをあてはめる
          checked: chk.checked, //もしチェックボックスなら"area_slug[]"がチェック状態（true/false）を取得
          areaId: chk.getAttribute("data-area-id"), //全ての"area_slug[]" 要素に設定された data-area-id 属性の値を取得
          name: chk.dataset.name
        };
      });
    }


    function blockOverLimitAreaSelection(limit) {
      if(limit > document.querySelectorAll(`${keyMap.area.query}:checked`).length) {
        return
      }

      document.querySelectorAll(`${keyMap.area.query}:not(:checked)`).forEach(e => {
        changeClickableStat(e.parentElement, false)
         e.setAttribute("disabled", "")
      })
    }


    /**
     * @param {array} prefectures
     */
    function createPrefectureListItem(prefectures, areaName) {
      const result = []

      const areaNameTitle = document.createElement("strong")
      areaNameTitle.textContent = areaName
      result.push(areaNameTitle)

      prefectures.map((pref) => {
        const listItem = createSearchListItem("pref", pref)
        const input = listItem.querySelector("input")

        if (userCheckState.prefecture[pref.slug] && userCheckState.prefecture[pref.slug].checked) {
          input.checked = true; //inputはチェック状態 = チェックを復元
        }

        input.addEventListener('change', function() {

          // NOTE: 下位条件削除
          if(!this.checked) {
            const prefId = parseInt(this.getAttribute("data-prefecture-id"))
            doUnselectRelatedRegionCondtion(prefId)
          }

          userCheckState.prefecture[this.value] = {
            checked: this.checked,
            prefectureId: this.getAttribute("data-prefecture-id"),
            name: this.dataset.name
          };

          updateAreaSelectedItems(panel);
        });

        result.push(listItem)
      })

      return result
    }

    async function showPrefectureList() {
      const areasList = panel.querySelector('#areas-list');
      if (!areasList) return;
      areasList.dataset.type = "prefecture"
      if (currentListType === 'area') { // エリア表示中なら最新のチェック状態を更新する
        saveAreaState();
      }

      // 保存した状態から、チェックされたareaのIDを収集
      const selectedAreaIds = new Set(); //重複しない値をもたないオブジェクト
      for (let key in userCheckState.area) { //チェックが保存されたareaをkeyにしてループ
        if (userCheckState.area[key].checked) { //チェックが保存されたareaがtrueのとき
          selectedAreaIds.add(userCheckState.area[key].areaId); //重複しない値をもたないオブジェクトにarea.idを追加
        }
      }

      const groupedByAreaPrefList = getPrefListGroupedByAreaId()

      areasList.replaceChildren(); //一度panelBodySecond内のareaをクリア

      // 選択されたエリアに対応する都道府県ノードを追加
      Array.from(selectedAreaIds).map((areaId,idx) => {
        const prefList = groupedByAreaPrefList.get(parseInt(areaId))

        if(selectedAreaIds.size > 1) {
          const areaName = areas.at(masterData.areas.findIndex((v) => v.id == areaId)).name
          const listItem = createPrefectureListItem(prefList, areaName)
          areasList.appendChild(createAcordionMenu(listItem.shift(), listItem, idx === 0))
        } else {
          createPrefectureListItem(prefList, null).map(item => areasList.appendChild(item))
        }
      });

      currentListType = 'prefecture'; //フラグ設定
      updateAreaSelectedItems(panel);
    }

    // 【市区町村リストの表示】チェックされた都道府県に対応する市区町村を表示

    function createRegionListItem(regions, prefName) {
      const result = []

      const prefNameTitle = document.createElement("strong")
      prefNameTitle.textContent = prefName
      result.push(prefNameTitle)

      regions.map((region) => {
        const listItem = createSearchListItem("region", region)
        const chk = listItem.querySelector("input")
        if (userCheckState.region[chk.value] && userCheckState.region[chk.value].checked) {
          chk.checked = true;
        }
        chk.addEventListener('change', function () {
          userCheckState.region[this.value] = {
            checked: this.checked,
            regionId: this.getAttribute("data-region-id"),
            name: this.dataset.name
          };
          updateAreaSelectedItems(panel);
        });

        result.push(listItem)
      })

      return result
    }

    async function showRegionList() {
      const areasList = panel.querySelector('#areas-list');
      if (!areasList) return;

      areasList.dataset.type = "region"

      if (currentListType === 'prefecture') { // 都道府県表示中なら最新のチェック状態を更新する
        savePrefectureState(panel);
      }

      const selectedPrefectureIds = new Set(); // savedPrefectureChecks からチェック済みの都道府県の ID を収集
      for (let key in userCheckState.prefecture) {
        if (userCheckState.prefecture[key].checked) {
          selectedPrefectureIds.add(userCheckState.prefecture[key].prefectureId);//重複しない値をもたないオブジェクトにprefecture.idを追加
        }
      }

      areasList.replaceChildren(); // クリア

      Array.from(selectedPrefectureIds).map((prefId, idx) => {
        const regionList = getRegionListGroupedByPrefId().get(parseInt(prefId))

        if(selectedPrefectureIds.size > 1) {
          const { prefectures } = masterData
          const prefName = prefectures.at(prefectures.findIndex((v) => v.id == prefId)).name
          const listItem = createRegionListItem(regionList, prefName)
          areasList.appendChild(createAcordionMenu(listItem.shift(), listItem, idx === 0))
        } else {
          createRegionListItem(regionList, null).map((item) => areasList.appendChild(item))
        }
      });
      currentListType = 'region';
      updateAreaSelectedItems(panel);
    }


    if (areas && Array.isArray(areas)) { //areasがあり、areasが配列のとき
      buildAreaList(areas);
    }


    // selectedBoxArea：エリアリスト表示用
    const selectedBoxArea = panel.querySelector('.selected-box.selectedArea'); //areaの選択窓
    if (selectedBoxArea) {
      selectedBoxArea.addEventListener('click', function () { //areaの選択窓をクリックしたとき
        if (currentListType !== 'area') {// エリア表示中でなければ、areasListを再生成してエリアリスト表示
          buildAreaList(areas);
          currentListType = 'area';
        }
      });
    }

    // selectedBoxPrefecture：都道府県表示用
    const selectedBoxPrefecture = panel.querySelector('.selected-box.selectedPrefecture');
    if (selectedBoxPrefecture) {
      selectedBoxPrefecture.addEventListener('click', function () {
        if (currentListType === 'prefecture') return;
        showPrefectureList();
      });
    }

    // 市区町村表示用クリックイベント
    const selectedBoxRegion = panel.querySelector('.selected-box.selectedRegion');
    if (selectedBoxRegion) {
      selectedBoxRegion.addEventListener('click', function () {
        if (currentListType === 'region') return;
        showRegionList();
      });
    }
  }

  // 「開催日で探す」関数
  function datesSection(form, panel, isNotEventsOrArea) {

    if (!panel.classList.contains('panel-dates')) {
      return
    }

    // カレンダー
    // NOTE: mode:"range" かつ minDate=本日 / maxDate=本日+上限ヶ月 とすることで
    //       「to が from より前にならない」「期間が上限ヶ月を超えない」を担保する
    let startDate = null;
    let endDate = null;
    let maxDate = new Date();
    maxDate = maxDate.setMonth(maxDate.getMonth() + Number(searchLimits().period_months));
    let minDate = new Date();
    // minDate = minDate.setDate(minDate.getDate());

    const datePicker = panel.querySelector("#datePicker");
    if (!datePicker) return;

    const calendarBox = document.querySelector('.flatpickr-calendar');

    function formatDateSet(panel, startDate, endDate) {// M月D日(W)の形式に変換
      function formatDate(date) {
        const weekdays = ["日", "月", "火", "水", "木", "金", "土"];
        const month = date.getMonth() + 1;
        const day = date.getDate();
        const weekday = weekdays[date.getDay()];
        return `${month}<span class="md-text">月</span>${day}<span class="md-text">日</span><span class="md-text">(${weekday})</span>`;
      }

      // fromInput を探し、なければ生成して form に追加
      let fromInput = form.querySelector('input[name="from"]');
      if (!fromInput && form) {
        fromInput = document.createElement("input");
        fromInput.type = "hidden";
        fromInput.id = "fromInput";
        fromInput.name = "from";
        form.appendChild(fromInput);
      }
      // toInput を探し、なければ生成して form に追加
      let toInput = form.querySelector('input[name="to"]');
      if (!toInput && form) {
        toInput = document.createElement("input");
        toInput.type = "hidden";
        toInput.id = "toInput";
        toInput.name = "to";
        form.appendChild(toInput);
      }
      // createInput(panel);
      panel.querySelector("#dateStartSelect").innerHTML = formatDate(startDate);
      panel.querySelector("#dateEndSelect").innerHTML = formatDate(endDate);
      fromInput.value = startDate.toISOString().split('T')[0];
      toInput.value = endDate.toISOString().split('T')[0];
    }


    const calendar = flatpickr(datePicker, {
      locale: "ja",
      mode: "range",
      dateFormat: "Y-m-d",
      defaultDate: new Date(),
      minDate: minDate,
      maxDate: maxDate,
      onReady: function () { //カレンダーを読み込んだときの挙動
        if (calendarBox) {
          calendarBox.classList.add('panel-body-second');
          calendarBox.style.width = 'auto';
        }
        const calendarBoxbar = panel.querySelector('.hidden-input');//panels内の.flatpickr-calendarの起点
        if (isNotEventsOrArea) { //イベント一覧以外の表示場所操作
          calendarBoxbar.style.left = '-127%';
          calendarBoxbar.style.top = '-28px';
        }
      },
      onOpen: function () { //カレンダーを表示するときの挙動
        deActivePanel(document.querySelector(".panel-body-second-active"))
        activePanel(calendar.calendarContainer)

        // panel-body-second(カレンダー)表示中は日付検索窓の枠をハイライト(.active)する
        panel.querySelectorAll('.calendar-container .date-output').forEach(el => el.classList.add('active'))
      },
      onClose: function (selectedDates) { //カレンダーを閉じるときの挙動
        // カレンダーが閉じたら日付検索窓の枠ハイライトを解除する
        clearSearchBoxHighlight()

        if (selectedDates.length === 2) {


          startDate = selectedDates[0];
          endDate = selectedDates[1];

          formatDateSet(panel, startDate, endDate);

          const dateInput = panel.querySelector("#datePicker").value;
          if (dateInput.includes("から")) { //初期値は「y-m-d から y-m-d」となっている
            const [fromDate, toDate] = dateInput.split(" から "); // 「から」で fromDate と toDate 分割

            panel.querySelector('input[name="from"]').value = fromDate; //input.valueは送信される値 「から」で分割されたfrom, name=valueの形式
            panel.querySelector('input[name="to"]').value = toDate; //「から」で分割されたto, name=valueの形式
          }


          //絞り込みボタン　選択窓に要素が入ったときに.active付与・解除
          toggleCfbActive(panel);
        }
      }

    });

    //マウスオーバーによる開閉制御
    const opt = {attributes: true, attributeFilter: ['class'], attributeOldValue: true}
    const observer = new MutationObserver((mutations) => {
      for(const mutation of mutations) {
        const isActiveCalendar = mutation.oldValue.split(" ").includes("panel-body-second-active")
        const willDeactiveCalandar = !calendar.calendarContainer.classList.contains("panel-body-second-active")
        const isAcceptableCloseOrder = isActiveCalendar && willDeactiveCalandar

        if(!isAcceptableCloseOrder) {
          return
        }

        observer.disconnect()
        calendar.close()
        observer.observe(calendar.calendarContainer, opt)
      }
    })

    observer.observe(calendar.calendarContainer, opt)

    panel.querySelector("#dateStart").addEventListener("mouseover", calendar.open)
    panel.querySelector("#dateEnd").addEventListener("mouseover",  calendar.open)

    //カレンダー開閉イベントを登録
    panel.querySelector("#dateStart").addEventListener("click", function () {
      calendar._positionElement.scrollIntoView({behavior: 'instant', block: 'nearest'})
      calendar.toggle();
    });
    panel.querySelector("#dateEnd").addEventListener("click", function () {
      calendar._positionElement.scrollIntoView({behavior: 'instant', block: 'nearest'})
      calendar.toggle();
    });


    // ページ読み込み時に日付を復元
    const urlParams = new URLSearchParams(window.location.search);
    const fromDate = urlParams.get("from");
    const toDate = urlParams.get("to");
    if (fromDate && toDate) { //パラメータにfromとtoがある場合
      startDate = new Date(fromDate);
      endDate = new Date(toDate);
      formatDateSet(panel, startDate, endDate);
      calendar.setDate([startDate, endDate]);
    }

    toggleCfbActive(panel)
  }

  // 「カテゴリーで探す」関数
  function categoriesSection(panel, categories) {

    if (!panel.classList.contains('panel-categories')) {
      return
    }

    // カテゴリーリストを作成
    if (categories && Array.isArray(categories)) {
      buildCategoryList(panel, categories);
    }
    // チェックボックスにチェックを入れたら選択窓に表示
    panel.addEventListener('change', function () {
      updateSelectedItems(panel);
    });
    toggleCfbActive(panel)
  }

  function buildCategoryList(panel, categories) {
    const categoriesList = panel.querySelector('#categories-list');
    if (!categoriesList) return;

    categories.forEach(category => {
      const item = createCheckboxItem(category, {
        inputName: "category_slug[]",
        labelTextProp: "name",
        slugProp: "slug",
        dataAttributes: { name: "name" }
      });
      categoriesList.appendChild(item);
    });
  }

  // 「メーカーで探す」関数
  function makersSection(panel, master) {

    if (!panel.classList.contains('panel-makers')) {
      return
    }

    // メーカーの表示と頭文字表示
    const indexMap = new Map([
      ['A', '英数字'], ['B', '英数字'], ['C', '英数字'], ['D', '英数字'], ['E', '英数字'],
      ['F', '英数字'], ['G', '英数字'], ['H', '英数字'], ['I', '英数字'], ['J', '英数字'],
      ['K', '英数字'], ['L', '英数字'], ['M', '英数字'], ['N', '英数字'], ['O', '英数字'],
      ['P', '英数字'], ['Q', '英数字'], ['R', '英数字'], ['S', '英数字'], ['T', '英数字'],
      ['U', '英数字'], ['V', '英数字'], ['W', '英数字'], ['X', '英数字'], ['Y', '英数字'],
      ['Z', '英数字'], ['0', '英数字'], ['1', '英数字'], ['2', '英数字'], ['3', '英数字'],
      ['4', '英数字'], ['5', '英数字'], ['6', '英数字'], ['7', '英数字'], ['8', '英数字'],
      ['9', '英数字'], ['ア', 'ア'], ['イ', 'ア'], ['ウ', 'ア'], ['エ', 'ア'], ['オ', 'ア'],
      ['カ', 'カ'], ['キ', 'カ'], ['ク', 'カ'], ['ケ', 'カ'], ['コ', 'カ'], ['ガ', 'カ'],
      ['ギ', 'カ'], ['グ', 'カ'], ['ゴ', 'カ'], ['サ', 'サ'], ['シ', 'サ'], ['ス', 'サ'],
      ['セ', 'サ'], ['ソ', 'サ'], ['ザ', 'サ'], ['ジ', 'サ'], ['ズ', 'サ'], ['ゼ', 'サ'],
      ['ゾ', 'サ'], ['タ', 'タ'], ['チ', 'タ'], ['ツ', 'タ'], ['テ', 'タ'], ['ト', 'タ'],
      ['ダ', 'タ'], ['ヂ', 'タ'], ['ヅ', 'タ'], ['デ', 'タ'], ['ド', 'タ'], ['ナ', 'ナ'],
      ['ニ', 'ナ'], ['ヌ', 'ナ'], ['ネ', 'ナ'], ['ノ', 'ナ'], ['ハ', 'ハ'], ['ヒ', 'ハ'],
      ['フ', 'ハ'], ['ヘ', 'ハ'], ['ホ', 'ハ'], ['バ', 'ハ'], ['ビ', 'ハ'], ['ブ', 'ハ'],
      ['ベ', 'ハ'], ['ボ', 'ハ'], ['パ', 'ハ'], ['ピ', 'ハ'], ['プ', 'ハ'], ['ペ', 'ハ'],
      ['ポ', 'ハ'], ['マ', 'マ'], ['ミ', 'マ'], ['ム', 'マ'], ['メ', 'マ'], ['モ', 'マ'],
      ['ヤ', 'ヤ'], ['ユ', 'ヤ'], ['ヨ', 'ヤ'], ['ラ', 'ラ'], ['リ', 'ラ'], ['ル', 'ラ'],
      ['レ', 'ラ'], ['ロ', 'ラ'], ['ワ', 'ワ'], ['ヲ', 'ワ'], ['ン', 'ワ'], ['その他', 'その他']
    ]);

    // ひらがな → カタカナ変換
    function toKatakana(text) {
      return text.replace(/[\u3041-\u3096]/g, match => String.fromCharCode(match.charCodeAt(0) + 0x60));
    }

    // メーカーリストを構築
    function buildMakerList(makers) {
      // メーカーをグループごとに分類
      const groups = new Map();
      makers.forEach(maker => {
        let char = maker.name_ruby === '' ? maker.name : maker.name_ruby;
        char = toKatakana(char.substring(0, 1)).toUpperCase(); // カタカナ化＆大文字化
        const index = indexMap.get(char) || 'その他';

        if (!groups.has(index)) {
          groups.set(index, []);
        }
        groups.get(index).push(maker);
      });

      const MakersKanaList = panel.querySelector('#makers-kana-list'); // .panel-body#makers-kana-list = MakersKanaList
      if (!MakersKanaList) return; //MakersKanaListがなければ処理終了

      // カナボタンの生成
      if (!MakersKanaList.querySelector('.index-list')) { // もし#makers-kana-listの中に.index-listがなければ
        const indexList = document.createElement('ul'); // ul = indexList
        indexList.classList.add('index-list'); //ul.index-list
        MakersKanaList.appendChild(indexList); // #makers-kana-list ul.index-list

        groups.forEach((_, key) => { // groupsという配列名で要素、順番、配列のうち、要素は無視してkeyという順番の引数を取る
          const listItem = document.createElement('li'); // listItem = liで作成
          const indexButton = document.createElement('button'); // indexButton = button で作成
          indexButton.classList.add('btn-default', 'search-list-item'); //button.btn-default.search-list-item
          indexButton.textContent = key; // buttonのなかにtext 順番（ここだとカナ）
          indexButton.type = 'button'; // button type= button
          indexButton.dataset.index = key; //button date-index 順番（ここだとカナ）
          listItem.appendChild(indexButton); // li button
          indexList.appendChild(listItem); // ul.index-list li button
        });
      } // 終了　もし#makers-kana-listの中に.index-listがなければ

      // 各カナグループのリストを作成
      const makersList = panel.querySelector('#makers-list'); // makersList = #makers-list ひとつ
      if (!makersList) return; // makersListがなければ処理終了

      groups.forEach((makers, key) => { // groupsという配列名で makersという要素とkeyという順番の2つの引数を取る
        const makersListContainer = document.createElement('div'); // div を生成
        makersList.appendChild(makersListContainer); // #makers-list div
        makersListContainer.id = `maker-${key}`; // #makers-list div#maker-${key（ここだとカナ）}
        makersListContainer.style.display = 'none';  // #makers-list div#maker-${key（ここだとカナ）}にdisplay:none;

        const header = document.createElement('p'); // header = p
        header.textContent = `「${key}」で始まるメーカー`; // <p>${key}」で始まるメーカー</p>
        header.classList.add('maker-kana'); // p.maker-kana
        makersListContainer.appendChild(header); // #makers-list div#maker-${key（ここだとカナ）} p.maker-kana

        const targetList = document.createElement('ul'); // list = ul
        targetList.classList.add('maker-list'); // ul.maker-list
        makersListContainer.appendChild(targetList); // #makers-list div#maker-${key（ここだとカナ）} ul.maker-list

        makers.forEach(maker => {
          const item = createCheckboxItem(maker, {
            inputName: "maker_slug[]",
            labelTextProp: "name",
            slugProp: "slug",
            dataAttributes: { name: "name" }
          });
          targetList.appendChild(item);
        });

      });
    }



    function toggleMakerPanel(index) { // indexという引数をとる

      document.querySelectorAll(".panel").forEach(panel => { // 配列panelsでpanelという要素を取って繰り返す
        if (panel.closest('.panel-makers')) { // もし.panelの同列または親要素に.panel-makersがある場合
          const makersListContainers = panel.querySelectorAll('#makers-list div'); // panel #makers-list divの全て = makersListContainers
          makersListContainers.forEach(container => { // panel #makers-list divの全てを配列名として要素containerとして繰り返す
            if (container.id === `maker-${index}` && container.style.display == 'none') { // もしpanel #makers-list divのidが#maker-${index}だったら
              container.style.display = 'block'; // panel #makers-list divにdisplay:block;
            } else { // それ以外は
              container.style.display = 'none';//panel #makers-list divにdisplay:none;
            }
          });
        }
      }); //終了　配列panelsでpanelという要素を使って繰り返す
    } // 終了 .panel-makers内の#maker-${index}にblock or none;


    // メーカーカナ のクリックで開閉
    function initializeMakerPanel(panel, makers) {
      // メーカーリストを生成
      buildMakerList(makers);

      if (panel.closest('.panel-makers')) {
        panel.querySelectorAll('#makers-list > div').forEach(makerContainer => {
          const index = makerContainer.id.replace('maker-', '');

          panel.querySelectorAll(`[data-index="${index}"]`).forEach(button => {
            button.addEventListener('click', function (event) {
              event.preventDefault();
              event.stopPropagation();
              toggleMakerPanel(index);
            });
          });
        });
      }
    }

    initializeMakerPanel(panel, master);
    setTimeout(() => updateSelectedItems(panel), 0);

    setTimeout(() => updateSelectedItems(panel), 0);
    panel.addEventListener('change', function () {
      updateSelectedItems(panel);
    });
  };

// ==================== restore searchParams ====================

function restoreSearchParams(data) {

  restoreArea()
  restoreMultipleArea()

  const params = new URLSearchParams(location.search)

  if(params.size <= 0) {
    return
  }

  const categoryPat = /category_slug\[\d*\]/
  const makerPat = /maker_slug\[\d*\]/
  const keyWordPat = /^q$/

  params.forEach((_,k) => {
    if(keyMap.pref.pattern.test(k)) {
      restorePref(params.getAll(k), data.prefectures)
    }
    if(keyMap.region.pattern.test(k)) {
      restoreRegion(params.getAll(k), data.regions)
    }
    if(categoryPat.test(k)) {
      restoreCategory(params.getAll(k))
    }
    if(makerPat.test(k)) {
      restoreMaker(params.getAll(k))
    }
    if(keyWordPat.test(k)) {
      document.querySelector("input[name='q']").textContent = params.get(k)
    }
  })
}

function restoreArea() {
  const pathName = location.pathname

  if(!pathName.includes("/area")) {
    return
  }

  const areaSlug = pathName.split('/').pop()

  const input = document.getElementById(areaSlug)
  input.checked = true
  input.dispatchEvent(new Event('change'))
}

function restoreMultipleArea() {
  const pathName = location.pathname

  if(!pathName.includes("/events")) {
    return
  }

  const areaSlugList = new URLSearchParams(location.search).getAll(keyMap.area.keyName)
  areaSlugList.map(slug => {
    const input = document.getElementById(slug)
    input.checked = true
    input.dispatchEvent(new Event('change'))
  })
}

function restorePref(params, master) {
  const container = document.querySelector(".selectedPrefecture #selectedItems")
  container.replaceChildren()

  params.map(param => {
    const idx = master.findIndex(v => v.slug == param)
    const prefInfo = master[idx]

    const li = document.createElement("li")
    li.textContent = prefInfo.name
    container.appendChild(li)

    userCheckState.prefecture[prefInfo.slug] = {
      checked: true,
      prefectureId: prefInfo.id,
      name: prefInfo.name
    };
  })
}

function restoreRegion(params, master) {
  const container = document.querySelector(".selectedRegion #selectedItems")
  container.replaceChildren()

  params.map(param => {
    let idx = master.findIndex(v => v.slug == param)

    if(idx === -1) {
      //FIXME: regions.slugはdefault nullのため管理画面にてslug変更可能になるまではidにフォールバックする
      idx = master.findIndex(v => v.id == param)
    }
    const regionInfo = master[idx]

    const li = document.createElement("li")
    li.textContent = regionInfo.name
    container.appendChild(li)

    userCheckState.region[regionInfo.slug] = {
      checked: true,
      regionId: regionInfo.id,
      name: regionInfo.name
    };
  })
}

function restoreCategory(params) {
  params.map(param => document.getElementById(param).checked = true)
  document.querySelector(".panel-categories").dispatchEvent(new Event('change'))
}

function restoreMaker(params) {
  params.map(param => {
    document.getElementById(param).checked = true
  })
  document.querySelector(".panel-makers").dispatchEvent(new Event('change'))
}

function createSearchListItem(type, data) {
  const attrMap = {
    pref: {
      inputName:  keyMap.pref.keyName,
      inputDataAttr: "data-prefecture-id"
    },
    region: {
      inputName: keyMap.region.keyName,
      inputDataAttr: "data-region-id"
    }
  }


  const listItem = document.createElement('li');
  listItem.classList.add('btn-default');

  const label = document.createElement('label');
  label.classList.add('search-list-item');
  //FIXME: regions.slugはdefault nullのため管理画面にてslug変更可能になるまではidにフォールバックする
  label.htmlFor = data.slug ?? data.id;
  label.textContent = data.name;

  const input = document.createElement('input');
  input.type = 'checkbox';
  //FIXME: regions.slugはdefault nullのため管理画面にてslug変更可能になるまではidにフォールバックする
  input.id = data.slug ?? data.id
  input.name = attrMap[type]['inputName'];
  //FIXME: regions.slugはdefault nullのため管理画面にてslug変更可能になるまではidにフォールバックする
  input.value = data.slug ?? data.id;
  input.dataset.name = data.name;
  input.setAttribute(attrMap[type]['inputDataAttr'], data.id);

  label.prepend(input)
  listItem.appendChild(label)
  return listItem
}

function createAcordionMenu(title, childList, isOpen = false) {
  const details = document.createElement("details")
  const summary = document.createElement("summary")

  summary.style.display = "list-item"
  summary.appendChild(title)
  details.appendChild(summary)
  details.classList.add("menu-item")
  details.open = isOpen;
  childList.map(child => {
    child.addEventListener("click", (e) => e.stopPropagation());
    details.appendChild(child)
  })

  // NOTE: 1度に開けるアコーディオンは1つに制限する
  details.addEventListener("click", () => {
    document.querySelector(".menu-item[open]").toggleAttribute("open")
  })

  return details
}

function savePrefectureState(panel) {
  const checkboxes = panel.querySelectorAll(keyMap.pref.query);
  checkboxes.forEach(chk => {
    userCheckState.prefecture[chk.value] = {
      checked: chk.checked,
      prefectureId: chk.getAttribute("data-prefecture-id"),
      name: chk.dataset.name
    };
  });
}
